import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import {
  fetchExpenses,
  fetchExpenseCategories,
  createExpense,
  updateExpense,
  deleteExpense,
} from "../redux/expenseSlice";
import "./css/AdminExpensesPage.css";

const Modal = ({ children, isOpen, onClose }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      {children}
    </div>,
    document.body
  );
};

const AdminExpensesPage = () => {
  const dispatch = useDispatch();

  const { items: expenses, categories } = useSelector(
    (state) => state.expenses
  );

  const [openDialog, setOpenDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    category: "",
    expenseDate: new Date().toISOString().split("T")[0],
    paymentMethod: "cash",
    receipt: "",
    notes: "",
  });

  const [filters, setFilters] = useState({
    category: "",
    startDate: "",
    endDate: "",
  });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    dispatch(fetchExpenses({}));
    dispatch(fetchExpenseCategories());
  }, [dispatch]);

  // Modal açıkken body scroll'unu engelle
  useEffect(() => {
    const isModalOpen = openDialog || confirmDelete;

    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [openDialog, confirmDelete]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        description: expense.description,
        amount: expense.amount,
        category: expense.category,
        expenseDate: new Date(expense.expenseDate).toISOString().split("T")[0],
        paymentMethod: expense.paymentMethod,
        receipt: expense.receipt || "",
        notes: expense.notes || "",
      });
    } else {
      setEditingExpense(null);
      setFormData({
        description: "",
        amount: "",
        category: "",
        expenseDate: new Date().toISOString().split("T")[0],
        paymentMethod: "cash",
        receipt: "",
        notes: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingExpense(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingExpense) {
        await dispatch(
          updateExpense({ id: editingExpense._id, data: formData })
        ).unwrap();
        showToast("Gider güncellendi");
      } else {
        await dispatch(createExpense(formData)).unwrap();
        showToast("Gider oluşturuldu");
      }
      handleCloseDialog();
      dispatch(fetchExpenses(filters));
    } catch (error) {
      showToast(error || "İşlem başarısız", "error");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteExpense(expenseToDelete)).unwrap();
      showToast("Gider silindi", "info");
      dispatch(fetchExpenses(filters));
    } catch {
      showToast("Silme başarısız", "error");
    } finally {
      setConfirmDelete(false);
      setExpenseToDelete(null);
    }
  };

  const handleApplyFilters = () => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== "")
    );
    dispatch(fetchExpenses(cleanFilters));
  };

  const getPaymentMethodText = (method) => {
    const methods = {
      cash: "Nakit",
      credit_card: "Kredi Kartı",
      bank_transfer: "Havale",
      other: "Diğer",
    };
    return methods[method] || method;
  };

  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);

  return (
    <div className="admin-expenses-page">
      <div className="page-container">
        <div className="page-header">
          <div className="header-info">
            <h1 className="page-title">Gider Yönetimi</h1>
            <div className="total-expense">
              Toplam Gider:{" "}
              <span className="amount">{totalExpenses.toFixed(2)} TL</span>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => handleOpenDialog()}
          >
            <span className="btn-icon">➕</span>
            Yeni Gider
          </button>
        </div>

        {/* Filtreler */}
        <div className="filters-section">
          <div className="filter-grid">
            <div className="filter-item">
              <label htmlFor="filter-category">Kategori</label>
              <select
                id="filter-category"
                value={filters.category}
                onChange={(e) =>
                  setFilters({ ...filters, category: e.target.value })
                }
                className="filter-select"
              >
                <option value="">Tümü</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-item">
              <label htmlFor="filter-start">Başlangıç</label>
              <input
                id="filter-start"
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value })
                }
                className="filter-input"
              />
            </div>

            <div className="filter-item">
              <label htmlFor="filter-end">Bitiş</label>
              <input
                id="filter-end"
                type="date"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
                className="filter-input"
              />
            </div>

            <div className="filter-item filter-btn-wrapper">
              <button
                className="btn btn-outlined filter-btn"
                onClick={handleApplyFilters}
              >
                Filtrele
              </button>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="expenses-table">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Açıklama</th>
                <th>Kategori</th>
                <th>Tutar</th>
                <th>Ödeme Yöntemi</th>
                <th className="actions-cell">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => (
                <tr key={expense._id}>
                  <td>
                    {new Date(expense.expenseDate).toLocaleDateString("tr-TR")}
                  </td>
                  <td>{expense.description}</td>
                  <td>
                    <span className="category-badge">{expense.category}</span>
                  </td>
                  <td>
                    <span className="expense-amount">
                      {expense.amount.toFixed(2)} TL
                    </span>
                  </td>
                  <td>{getPaymentMethodText(expense.paymentMethod)}</td>
                  <td className="actions-cell">
                    <button
                      className="icon-btn icon-btn-edit"
                      onClick={() => handleOpenDialog(expense)}
                      title="Düzenle"
                    >
                      ✏️
                    </button>
                    <button
                      className="icon-btn icon-btn-delete"
                      onClick={() => {
                        setExpenseToDelete(expense._id);
                        setConfirmDelete(true);
                      }}
                      title="Sil"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {expenses.length === 0 && (
            <div className="empty-state">
              <p>Henüz gider yok</p>
            </div>
          )}
        </div>
      </div>

      {/* Gider Ekleme/Düzenleme Dialog */}
      <Modal isOpen={openDialog} onClose={handleCloseDialog}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editingExpense ? "Gider Düzenle" : "Yeni Gider"}</h2>
            <button className="modal-close" onClick={handleCloseDialog}>
              ✕
            </button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group form-group-full">
                <label htmlFor="description">
                  Açıklama <span className="required">*</span>
                </label>
                <input
                  id="description"
                  type="text"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Gider açıklaması"
                />
              </div>

              <div className="form-group">
                <label htmlFor="amount">
                  Tutar (TL) <span className="required">*</span>
                </label>
                <input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      amount: parseFloat(e.target.value),
                    })
                  }
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">
                  Kategori <span className="required">*</span>
                </label>
                <input
                  id="category"
                  type="text"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  placeholder="Örn: Kira, Elektrik, Malzeme"
                />
              </div>

              <div className="form-group">
                <label htmlFor="expenseDate">
                  Tarih <span className="required">*</span>
                </label>
                <input
                  id="expenseDate"
                  type="date"
                  value={formData.expenseDate}
                  onChange={(e) =>
                    setFormData({ ...formData, expenseDate: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label htmlFor="paymentMethod">Ödeme Yöntemi</label>
                <select
                  id="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={(e) =>
                    setFormData({ ...formData, paymentMethod: e.target.value })
                  }
                >
                  <option value="cash">Nakit</option>
                  <option value="credit_card">Kredi Kartı</option>
                  <option value="bank_transfer">Havale</option>
                  <option value="other">Diğer</option>
                </select>
              </div>

              <div className="form-group form-group-full">
                <label htmlFor="receipt">Fatura/Makbuz No</label>
                <input
                  id="receipt"
                  type="text"
                  value={formData.receipt}
                  onChange={(e) =>
                    setFormData({ ...formData, receipt: e.target.value })
                  }
                  placeholder="Fatura veya makbuz numarası"
                />
              </div>

              <div className="form-group form-group-full">
                <label htmlFor="notes">Notlar</label>
                <textarea
                  id="notes"
                  rows="3"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder="Ek notlar..."
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleCloseDialog}>
              İptal
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={
                !formData.description ||
                !formData.amount ||
                !formData.category ||
                !formData.expenseDate
              }
            >
              {editingExpense ? "Güncelle" : "Oluştur"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Silme Onayı Dialog */}
      <Modal isOpen={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <div
          className="modal-content modal-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>Gideri Sil</h2>
            <button
              className="modal-close"
              onClick={() => setConfirmDelete(false)}
            >
              ✕
            </button>
          </div>
          <div className="modal-body">
            <p>
              Bu gideri silmek istediğinizden emin misiniz? Bu işlem geri
              alınamaz.
            </p>
          </div>
          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={() => setConfirmDelete(false)}
            >
              İptal
            </button>
            <button className="btn btn-danger" onClick={handleDeleteConfirm}>
              Sil
            </button>
          </div>
        </div>
      </Modal>

      {/* Toast */}
      <Snackbar
        open={toast.show}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, show: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.type}>{toast.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default AdminExpensesPage;
